import { create } from "zustand";
import { RootState, INITIAL_STATE } from "../core/state-shape";
import { measure } from "@/lib/performance";
import { http } from "@/lib/http";
import { toast } from "sonner";
export type SettingsState = RootState["settings"];

// Debounce for saving settings
let saveTimeout: ReturnType<typeof setTimeout> | null = null;

type SettingsActions = {
  setState: (newState: SettingsState) => void;
  patch: (p: Partial<SettingsState>) => void;
  reset: () => void;
  saveProviderLimits: (limits: Record<string, any>) => Promise<void>;
  saveSettings: (settings: SettingsState) => Promise<void>;
};

export const useSettingsStore = create<SettingsState & SettingsActions>((set, get) => ({
  ...INITIAL_STATE.settings,
  setState: (newState) => set(newState),
  patch: (p: Partial<SettingsState>) => {
    // Optimistically update the UI
    set(p);

    // Debounce saving to the backend
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(() => {
      const { llmProviders, ...otherSettings } = get();

      // Prepare general settings for saving (excluding store actions)
      const { patch, reset, setState, saveSettings, saveProviderLimits, ...settingsToSave } = otherSettings;
      get().saveSettings(settingsToSave as SettingsState);

      // Save providers to their own endpoint
      const providersList = Object.values(llmProviders || {});
      http('/api/settings/llm-providers', {
        method: 'PUT',
        body: JSON.stringify(providersList),
        sendProjectId: false,
      }).then(() => {
        console.log("LLM Providers saved to backend.");
      }).catch((e: any) => {
        toast.error("Failed to save LLM Providers.", { description: e.message });
      });
    }, 1000); // Debounce for 1 second
  },
  reset: () => set({ ...INITIAL_STATE.settings }),
  saveSettings: measure(async (settingsToSave) => {
    try {
      await http('/api/settings', {
          method: 'POST',
          body: JSON.stringify({ settings: settingsToSave }), // settingsToSave already excludes llmProviders
          sendProjectId: false
      });
      console.log("Settings saved to backend.");
    } catch (e: any) {
        console.error("Failed to save settings:", e);
        toast.error("Failed to save settings.", { description: e.message });
    }
  }, 'settings.saveSettings'),
  saveProviderLimits: measure(async (limits) => {
    try {
        await http('/api/settings/save-provider-limits', {
          method: 'POST',
          body: JSON.stringify({ limits }),
          sendProjectId: false
        });
    } catch (e: any) {
        toast.error("Failed to save provider limits.", { description: e.message });
    }
  }, 'settings.saveProviderLimits'),
}));

